// Sign-in providers. DevAuth: email only, against the API's local-only POST /dev/login.
// SupabaseAuth: email + password, used when VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY
// are set. Either way the result is a bearer token for our API, which GET /me then checks.

import { DEV_ACCESS_HEADER, devLoginResponseSchema } from '@enp/domain/api';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { api, apiBaseUrl, ApiError, isLoopbackApi } from '../lib/api.js';
import type { StoredSession } from './token-store.js';

export interface SignInInput {
  email: string;
  password?: string;
  /** Dev sign-in from another device on the network: DEV_ACCESS_CODE from `.env.local`. */
  devAccessCode?: string;
}

export interface AuthProvider {
  readonly kind: 'dev' | 'supabase';
  /** Whether the sign-in form needs a password field. */
  readonly needsPassword: boolean;
  /** Whether the sign-in form offers the dev access code field. */
  readonly offersDevAccessCode: boolean;
  /**
   * Returns the session (an API bearer token, plus a refresh token when the provider has
   * one), or throws SignInError with a message for the user.
   */
  signIn(input: SignInInput): Promise<StoredSession>;
  /**
   * Swaps a refresh token for a new session (B-001). Absent when the provider has none
   * (dev tokens last 12 hours; the session then ends at expiry, with a clear message).
   */
  refresh?(refreshToken: string): Promise<RefreshResult>;
  /**
   * Ends the session with the provider. `session` is the stored session: after a reload the
   * provider's client holds nothing in memory, so it needs the stored tokens to revoke them.
   */
  signOut(session?: StoredSession | null): Promise<void>;
}

/**
 * `rejected`: the refresh token is no good any more (signed out elsewhere, revoked, used):
 * the session is over. `unavailable`: couldn't ask (offline, server error): try again.
 */
export type RefreshResult =
  { status: 'ok'; session: StoredSession } | { status: 'rejected' } | { status: 'unavailable' };

/** A sign-in failure the user can act on (message is shown as-is). */
export class SignInError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SignInError';
  }
}

export class DevAuth implements AuthProvider {
  readonly kind = 'dev' as const;
  readonly needsPassword = false;
  /**
   * The local API answers /dev/* from this computer freely; from any other device only
   * with the dev access code. So the field is offered when the API is not on loopback.
   */
  readonly offersDevAccessCode: boolean;

  constructor(apiUrl: string = apiBaseUrl()) {
    this.offersDevAccessCode = !isLoopbackApi(apiUrl);
  }

  async signIn({ email, devAccessCode }: SignInInput): Promise<StoredSession> {
    const code = devAccessCode?.trim();
    try {
      const { token } = await api('/dev/login', {
        method: 'POST',
        body: { email },
        token: null,
        headers: code ? { [DEV_ACCESS_HEADER]: code } : undefined,
        schema: devLoginResponseSchema,
      });
      return { token };
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        throw new SignInError(
          code
            ? 'That dev access code wasn’t accepted. Check DEV_ACCESS_CODE in .env.local on the computer running the API.'
            : 'Dev sign-in from this device needs the dev access code (DEV_ACCESS_CODE in .env.local on the computer running the API).',
        );
      }
      if (error instanceof ApiError && error.status < 500) {
        throw new SignInError(error.message);
      }
      throw error;
    }
  }

  async signOut(): Promise<void> {
    // Dev tokens are stateless; forgetting the token is enough.
  }
}

export class SupabaseAuth implements AuthProvider {
  readonly kind = 'supabase' as const;
  readonly needsPassword = true;
  readonly offersDevAccessCode = false;
  private readonly client: SupabaseClient;

  constructor(url: string, anonKey: string) {
    // The Admin keeps its own token (memory + sessionStorage), so Supabase must not persist.
    this.client = createClient(url, anonKey, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
  }

  async signIn({ email, password }: SignInInput): Promise<StoredSession> {
    if (!password) throw new SignInError('Enter your password.');
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error || !data.session) {
      throw new SignInError(
        error?.status === 400 || error?.status === 401
          ? 'That email and password don’t match an account. Check the spelling, or ask the owner to add you.'
          : `Sign-in failed: ${error?.message ?? 'no session returned'}.`,
      );
    }
    return { token: data.session.access_token, refreshToken: data.session.refresh_token };
  }

  async refresh(refreshToken: string): Promise<RefreshResult> {
    let result: Awaited<ReturnType<SupabaseClient['auth']['refreshSession']>>;
    try {
      result = await this.client.auth.refreshSession({ refresh_token: refreshToken });
    } catch {
      return { status: 'unavailable' };
    }
    const { data, error } = result;
    if (data.session) {
      return {
        status: 'ok',
        session: { token: data.session.access_token, refreshToken: data.session.refresh_token },
      };
    }
    // A 4xx answer is Auth refusing this refresh token; anything else (no status: the
    // request never got through; 5xx) may clear by itself.
    const status = error?.status;
    return status !== undefined && status >= 400 && status < 500
      ? { status: 'rejected' }
      : { status: 'unavailable' };
  }

  async signOut(session?: StoredSession | null): Promise<void> {
    // The client doesn't persist its session (see the constructor), so after a reload it
    // holds none and signOut() alone would revoke nothing: the refresh token would stay good
    // at Auth (CR8B-8). Restore the session from the stored tokens first, then sign out.
    const { data } = await this.client.auth.getSession();
    if (!data.session && session?.refreshToken) {
      const restored = await this.client.auth.setSession({
        access_token: session.token,
        refresh_token: session.refreshToken,
      });
      // Auth already refuses the refresh token (revoked, used): nothing is left to revoke.
      if (restored.error || !restored.data.session) return;
    }
    await this.client.auth.signOut();
  }
}

/** Picks the provider from the build's environment. */
export function createAuthProvider(
  env: { VITE_SUPABASE_URL?: string; VITE_SUPABASE_ANON_KEY?: string } = import.meta.env,
): AuthProvider {
  const url = env.VITE_SUPABASE_URL?.trim();
  const key = env.VITE_SUPABASE_ANON_KEY?.trim();
  return url && key ? new SupabaseAuth(url, key) : new DevAuth();
}
